import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import PFLogo from '../assets/logo.svg';
import './NavbarNew.css';

const mobileBreakpoint = 992;

function NavbarNew() {
  const { i18n, t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;

  const currentLang = i18n.language || 'bs';

  const menuItems = useMemo(() => {
    // Prefix is empty for default language (bs), or /en for english
    const prefix = currentLang === 'bs' ? '' : `/${currentLang}`;

    return [
      { label: 'nav.home', path: `${prefix}/` },
      {
        label: 'nav.adopt',
        path: `${prefix}/adoption`,
        dropdown: [
          { label: 'nav.adopt', path: `${prefix}/adoption` },
          { label: 'nav.adoption_form', path: `${prefix}/adoption-form` },
          { label: 'nav.happy_endings', path: `${prefix}/happyendings` },
          { label: 'nav.faq', path: `${prefix}/faq` },
        ],
      },
      {
        label: 'nav.how_to_help',
        path: `${prefix}/donate`,
        dropdown: [
          { label: 'nav.donate', path: `${prefix}/donate` },
          { label: 'nav.sponsor', path: `${prefix}/sponsor` },
          { label: 'nav.volunteer', path: `${prefix}/volunteer` },
        ],
      },
      {
        label: 'nav.about',
        path: `${prefix}/about`,
        dropdown: [
          { label: 'nav.about_us', path: `${prefix}/about` },
          { label: 'PawFriends Sarajevo in Denmark', url: 'https://pawfriendsdenmark.org/', isExternal: true },
        ],
      },
      { label: 'nav.news', path: `${prefix}/news` },
      {
        label: 'nav.contact',
        path: `${prefix}/contact`,
      },
    ];
  }, [currentLang]);

  const [isSticky, setIsSticky] = useState(false);
  const [showTopBar, setShowTopBar] = useState(true);
  const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
  const [isMobile, setIsMobile] = useState(
    window.innerWidth <= mobileBreakpoint
  );
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Handle scroll behavior
  useEffect(() => {
    const handleScroll = () => {
      const scroll = window.scrollY;
      setIsSticky(scroll >= 60);
      setShowTopBar(scroll < 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = window.innerWidth <= mobileBreakpoint;
      setIsMobile(newIsMobile);
      // Close mobile menu if switching to desktop
      if (!newIsMobile) {
        setIsMenuOpen(false);
        setOpenDropdownIndex(null);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      // Prevent body scroll without changing position (no visual jump)
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
      // Prevent scrolling on iOS
      document.body.style.position = 'relative';
    } else {
      // Restore body scroll
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
      document.body.style.position = '';
    }

    return () => {
      // Cleanup on unmount
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
      document.body.style.position = '';
    };
  }, [isMenuOpen]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
    setOpenDropdownIndex(null);
  }, [location.pathname]);

  // Close mobile menu function
  const closeMenu = () => {
    setIsMenuOpen(false);
    setOpenDropdownIndex(null);
  };

  // Toggle mobile menu
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    if (isMenuOpen) {
      setOpenDropdownIndex(null);
    }
  };

  // Handle dropdown toggle (mobile)
  const handleDropdownToggle = index => {
    if (isMobile) {
      setOpenDropdownIndex(openDropdownIndex === index ? null : index);
    }
  };

  const isActive = path => {
    // Exact match handling
    if (path === '/' || path === `/${currentLang}/`) {
      // Special case for root paths
      return currentPath === path || currentPath === path.slice(0, -1);
    }
    return currentPath === path || currentPath.startsWith(path + '/');
  };

  // Get language text
  const getLanguageText = () => {
    return i18n.language === 'bs' ? 'BS' : 'EN';
  };

  const changeLanguage = lang => {
    const search = location.search;
    let newPath;

    // Ensure we clean existing prefix
    let cleanPath = currentPath;
    if (currentPath.startsWith('/en')) {
      cleanPath = currentPath.substring(3);
    }
    // If path was just /en, cleanPath is empty string, make it /
    if (!cleanPath) cleanPath = '/';

    if (lang === 'bs') {
      // No prefix for bs
      newPath = cleanPath;
    } else {
      // Add prefix for others
      // Ensure we don't double slash
      newPath = `/${lang}${cleanPath === '/' ? '' : cleanPath}`;
    }

    navigate(newPath + search);
    if (isMobile) {
      closeMenu();
    }
  };

  return (
    <nav className={`pf-navbar ${isSticky ? 'pf-navbar-sticky' : ''}`}>
      {showTopBar && !isMobile && (
        <div className="pf-navbar-top-bar">
          <div className="pf-navbar-top-bar-container">
            <div className="pf-navbar-top-bar-content">
              <div className="pf-navbar-top-bar-left">
                <ul className="pf-navbar-contact-details">
                  <li>
                    <i className="fa fa-envelope"></i>
                    <a href="mailto:pawfriendssarajevo@gmail.com">
                      pawfriendssarajevo@gmail.com
                    </a>
                  </li>
                </ul>
              </div>
              <div className="pf-navbar-top-bar-right">
                <ul className="pf-navbar-social-list">
                  <li>
                    <a
                      title="Facebook"
                      href="https://www.facebook.com/pawfriendssarajevo"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="fab fa-facebook-f"></i>
                    </a>
                  </li>
                  <li>
                    <a
                      title="Instagram"
                      href="https://www.instagram.com/pawfriends_sarajevo/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="fab fa-instagram"></i>
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="pf-navbar-main">
        <div className="pf-navbar-main-container">
          {/* Logo */}
          <Link
            className="pf-navbar-brand"
            to={currentLang === 'bs' ? '/' : `/${currentLang}`}
            onClick={() => {
              closeMenu();
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          >
            <img src={PFLogo} alt="Logo" width={40} style={{ fill: 'white' }} />
            <span className="pf-navbar-brand-text">
              <span className="pf-navbar-brand-main">PawFriends</span>
              <span className="pf-navbar-brand-sub"> Sarajevo</span>
            </span>
          </Link>

          {/* Right group — pushed to far right via ml-auto */}
          <div className="pf-navbar-right">

            {/* Nav menu (fullscreen overlay on mobile, inline on desktop) */}
            <div className={`pf-navbar-menu ${isMenuOpen ? 'pf-navbar-menu-open' : ''}`}>
              <ul className="pf-navbar-nav">
                {menuItems.map((item, index) => {
                  const isDropdownActive =
                    isActive(item.path) ||
                    (item.dropdown && item.dropdown.some(d => isActive(d.path)));
                  const isActiveTop = isActive(item.path);

                  if (item.dropdown) {
                    const isDropdownOpen = openDropdownIndex === index;
                    return (
                      <li
                        key={index}
                        className={`pf-navbar-nav-item pf-navbar-nav-item-dropdown ${isDropdownActive ? 'pf-navbar-nav-item-active' : ''} ${isDropdownOpen ? 'pf-navbar-nav-item-dropdown-open' : ''}`}
                        onMouseEnter={() => !isMobile && setOpenDropdownIndex(index)}
                        onMouseLeave={() => !isMobile && setOpenDropdownIndex(null)}
                      >
                        <Link
                          className="pf-navbar-nav-link pf-navbar-nav-link-dropdown"
                          to={item.path}
                          onClick={e => {
                            if (isMobile) {
                              e.preventDefault();
                              handleDropdownToggle(index);
                            }
                          }}
                        >
                          {t(item.label)}
                          <i className="fas fa-caret-down pf-navbar-dropdown-caret"></i>
                        </Link>
                        <div className={`pf-navbar-dropdown-menu ${isDropdownOpen ? 'pf-navbar-dropdown-menu-open' : ''}`}>
                          {item.dropdown.map((dropItem, dropIndex) =>
                            dropItem.isExternal ? (
                              <a
                                key={dropIndex}
                                className="pf-navbar-dropdown-item"
                                href={dropItem.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => { if (isMobile) closeMenu(); }}
                              >
                                {t(dropItem.label)}
                              </a>
                            ) : (
                              <Link
                                key={dropIndex}
                                className={`pf-navbar-dropdown-item ${isActive(dropItem.path) ? 'pf-navbar-dropdown-item-active' : ''}`}
                                to={dropItem.path}
                                onClick={() => { if (isMobile) closeMenu(); }}
                              >
                                {t(dropItem.label)}
                              </Link>
                            )
                          )}
                        </div>
                      </li>
                    );
                  }

                  return (
                    <li
                      key={index}
                      className={`pf-navbar-nav-item ${isActiveTop ? 'pf-navbar-nav-item-active' : ''}`}
                    >
                      <Link
                        className={`pf-navbar-nav-link ${isActiveTop ? 'pf-navbar-nav-link-active' : ''}`}
                        to={item.path}
                        onClick={() => { if (isMobile) closeMenu(); }}
                      >
                        {t(item.label)}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Language toggle — single button, switches on click */}
            <button
              className="pf-lang-toggle"
              onClick={() => changeLanguage(i18n.language === 'bs' ? 'en' : 'bs')}
              title={i18n.language === 'bs' ? 'Switch to English' : 'Prebaci na Bosanski'}
            >
              <i className="fas fa-globe" style={{ marginRight: '5px', fontSize: '0.85em' }}></i>
              {i18n.language != 'bs' ? 'BS' : 'EN'}
            </button>

            {/* Hamburger toggle — visible only on mobile */}
            <button
              className={`pf-navbar-toggle ${isMenuOpen ? 'pf-navbar-toggle-open' : ''}`}
              type="button"
              onClick={toggleMenu}
              aria-label="Toggle navigation"
              aria-expanded={isMenuOpen}
            >
              <span className="pf-navbar-toggle-icon">
                <span className="pf-navbar-toggle-line pf-navbar-toggle-line-top"></span>
                <span className="pf-navbar-toggle-line pf-navbar-toggle-line-middle"></span>
                <span className="pf-navbar-toggle-line pf-navbar-toggle-line-bottom"></span>
              </span>
            </button>

          </div>
        </div>
      </div>
    </nav>
  );
}

export default NavbarNew;
